import { atBusinessTime, BUSINESS_TIME_ZONE, Temporal } from '@rental/domain'

/**
 * Dates and times as staff type them: Europe/Rome wall-clock, whatever the
 * device's own zone, turned into instants only when sent. The conversion is
 * the domain's own (`atBusinessTime`), tested across the clock changes.
 */

/** Today's date and the time now, on Ischia. */
export function romeNow(now: Date = new Date()): { date: string; time: string } {
  const zoned = Temporal.Instant.fromEpochMilliseconds(now.getTime()).toZonedDateTimeISO(
    BUSINESS_TIME_ZONE,
  )
  return {
    date: zoned.toPlainDate().toString(),
    time: zoned.toPlainTime().toString({ smallestUnit: 'minute' }),
  }
}

/**
 * How long until the next minute begins, a little after it, so a clock never
 * shows the minute that is ending (the lock screen's, UI-2a). Rome's offsets
 * are whole hours: its minutes turn when UTC's do.
 */
export function untilNextMinute(epochMs: number): number {
  return 60_000 - (epochMs % 60_000) + 50
}

/** "09:52" to "09:55": a walk-in's start, a little ahead rather than behind. */
export function roundUp(time: string, step = 5): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  const total = Math.min(Math.ceil((hours * 60 + minutes) / step) * step, 23 * 60 + 55)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** The instant a Rome date and time happen, for the API; null when either is not valid. */
export function romeInstant(date: string, time: string): string | null {
  try {
    return atBusinessTime(date, time).toISOString()
  } catch {
    return null
  }
}

/** Where each of `days` Rome days starts, plus where the last one ends (days + 1 instants). */
export function dayBoundaries(from: string, days: number): number[] {
  const start = Temporal.PlainDate.from(from)
  return Array.from({ length: days + 1 }, (_, index) =>
    atBusinessTime(start.add({ days: index }), '00:00').getTime(),
  )
}
